import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '../ui';
import {
    columnLabel, moveColumn, moveColumnTo, setColumnVisible,
} from '../../utils/inventoryColumns';
import { ArrowDownIcon, ArrowUpIcon, GripIcon } from './icons';

const cssKey = (key) => String(key).replace(/["\\]/g, '\\$&');
const NONE = [];

/**
 * The column layout editor list, shared by the Columns picker (inventory)
 * and the admin's role preset editor: a checkbox per movable column (show /
 * hide), Move up / Move down buttons (keyboard and screen-reader reorder,
 * WCAG 2.5.7) and a native drag handle for pointer users. Focus stays on
 * the moved column's button. Every change is reported through `onAnnounce`
 * (the caller owns the polite live region).
 *
 * `forced`: keys shown regardless of the layout (Deal status while
 * committed units are included); their checkbox is checked and disabled.
 */
function ColumnLayoutList({
    layout, forced = NONE, onChange, onAnnounce, label = 'Column order', disabled = false, idPrefix = 'inv2-cols',
}) {
    const [dropKey, setDropKey] = useState(null);
    const listRef = useRef(null);
    const dragKey = useRef(null);
    // After a move: which button to keep focus on ({ key, dir }).
    const focusAfter = useRef(null);
    const n = layout.length;
    const announce = (text) => { if (onAnnounce) onAnnounce(text); };

    useEffect(() => {
        const target = focusAfter.current;
        if (!target || !listRef.current) return;
        focusAfter.current = null;
        const find = (dir) => listRef.current.querySelector(`[data-col="${cssKey(target.key)}"][data-dir="${dir}"]`);
        const same = find(target.dir);
        const other = find(target.dir === 'up' ? 'down' : 'up');
        (same && !same.disabled ? same : other)?.focus();
    }, [layout]);

    const announceMove = (next, key) => {
        const at = next.findIndex((c) => c.key === key);
        announce(`${columnLabel(key)} moved to position ${(at + 1).toLocaleString('en-US')} of ${next.length.toLocaleString('en-US')}.`);
    };

    const move = (key, dir) => {
        const next = moveColumn(layout, key, dir === 'up' ? -1 : 1);
        focusAfter.current = { key, dir };
        announceMove(next, key);
        onChange(next);
    };

    const toggle = (key, visible) => {
        announce(`${columnLabel(key)} ${visible ? 'shown' : 'hidden'}.`);
        onChange(setColumnVisible(layout, key, visible));
    };

    // Native drag and drop (pointer only; the buttons are the accessible way).
    const onDragStart = (key) => (e) => {
        dragKey.current = key;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', key); } catch (err) { /* some browsers refuse */ }
        }
    };
    const onDragOver = (key) => (e) => {
        if (!dragKey.current) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (dropKey !== key) setDropKey(key);
    };
    const onDrop = (key) => (e) => {
        e.preventDefault();
        const from = dragKey.current;
        dragKey.current = null;
        setDropKey(null);
        if (!from || from === key) return;
        const next = moveColumnTo(layout, from, layout.findIndex((c) => c.key === key));
        announceMove(next, from);
        onChange(next);
    };
    const onDragEnd = () => {
        dragKey.current = null;
        setDropKey(null);
    };

    return (
        <ol className="inv2-cols-list" aria-label={label} ref={listRef}>
            {layout.map((c, i) => {
                const name = columnLabel(c.key);
                const isForced = forced.includes(c.key);
                return (
                    <li
                        key={c.key}
                        className={`inv2-cols-item${dropKey === c.key ? ' is-drop' : ''}`}
                        draggable={!disabled}
                        onDragStart={disabled ? undefined : onDragStart(c.key)}
                        onDragOver={disabled ? undefined : onDragOver(c.key)}
                        onDrop={disabled ? undefined : onDrop(c.key)}
                        onDragEnd={disabled ? undefined : onDragEnd}
                    >
                        <span className="inv2-cols-grip" aria-hidden="true" title="Drag to reorder"><GripIcon /></span>
                        {/* The forced note goes on its own line under the name, so it
                            never widens the row (QA E2); it describes the checkbox. */}
                        <span className="inv2-cols-text">
                            <label className="inv2-cols-label">
                                <input
                                    type="checkbox"
                                    checked={c.visible || isForced}
                                    disabled={disabled || isForced}
                                    aria-describedby={isForced ? `${idPrefix}-forced-${c.key}` : undefined}
                                    onChange={(e) => toggle(c.key, e.target.checked)}
                                />
                                <span className="inv2-cols-name">{name}</span>
                            </label>
                            {isForced && (
                                <span className="inv2-cols-forced" id={`${idPrefix}-forced-${c.key}`}>Shown with committed units</span>
                            )}
                        </span>
                        <span className="inv2-cols-moves">
                            <IconButton
                                label={`Move ${name} up`}
                                className="inv2-cols-mv"
                                data-col={c.key}
                                data-dir="up"
                                disabled={disabled || i === 0}
                                onClick={() => move(c.key, 'up')}
                            >
                                <ArrowUpIcon />
                            </IconButton>
                            <IconButton
                                label={`Move ${name} down`}
                                className="inv2-cols-mv"
                                data-col={c.key}
                                data-dir="down"
                                disabled={disabled || i === n - 1}
                                onClick={() => move(c.key, 'down')}
                            >
                                <ArrowDownIcon />
                            </IconButton>
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

ColumnLayoutList.propTypes = {
    layout: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, visible: PropTypes.bool.isRequired })).isRequired,
    forced: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    onAnnounce: PropTypes.func,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    idPrefix: PropTypes.string,
};

export default ColumnLayoutList;
